<script setup lang="ts">
  import Badge from '@leaflink/stash/Badge.vue';
  import Button from '@leaflink/stash/Button.vue';
  import Divider from '@leaflink/stash/Divider.vue';
  import Dropdown from '@leaflink/stash/Dropdown.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Loading from '@leaflink/stash/Loading.vue';
  import Logo from '@leaflink/stash/Logo.vue';
  import useModals from '@leaflink/stash/useModals';
  import useToasts from '@leaflink/stash/useToasts';
  import { setPersistentItem } from '@leaflink/stash/utils/storage';
  import { storeToRefs } from 'pinia';
  import { computed, ref, useTemplateRef, watch } from 'vue';
  import { I18nT } from 'vue-i18n';
  import { useRoute, useRouter } from 'vue-router';

  import { APPS, ROUTE_NAMES } from '@/constants';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import i18n from '@/plugins/i18n';
  import {
    useAcceptPostTermsMutation,
    useAcknowledgePostEnrollmentMutation,
    usePaymentOnSellThroughEnrollmentQuery,
    useRejectPostEnrollmentMutation,
    useRemovePostEnrollmentCompletMutation,
  } from '@/queries/paymentOnSellThrough/paymentOnSellThroughEnrollment.query';

  import PaymentOnSellThroughEnrollmentModal from './PaymentOnSellThroughEnrollmentModal.vue';
  import PostEnrollmentNotInterestedModal from './PostEnrollmentNotInterestedModal.vue';
  import PostEnrollmentStepAction, { PostEnrollmentStepActionStatus } from './PostEnrollmentStepAction.vue';
  import PostEnrollmentTermsAndConditions from './PostEnrollmentTermsAndConditions.vue';

  const isRedirecting = ref(false);
  const modals = useModals();
  const { data: enrollmentQuery, isFetching } = usePaymentOnSellThroughEnrollmentQuery();
  const { mutateAsync: acknowledgeEnrollment } = useAcknowledgePostEnrollmentMutation();
  const { mutateAsync: rejectEnrollment } = useRejectPostEnrollmentMutation();
  const { mutateAsync: acceptTerms } = useAcceptPostTermsMutation();
  const { mutateAsync: removeEnrollmentCompletionMessage } = useRemovePostEnrollmentCompletMutation();
  const companyStore = useCompanyStore();
  const { activeCompany } = storeToRefs(companyStore);
  const router = useRouter();
  const route = useRoute();
  const toasts = useToasts();

  const dropdown = useTemplateRef('dropdown');

  const showDropdown = ref(false);

  const ENROLLMENT_MODAL_KEYS = {
    INITIAL: 'ENROLLMENT_INITIAL',
    REJECT: 'ENROLLMENT_REJECT',
    TERMS: 'ENROLLMENT_TERMS',
  };

  function openEnrollmentModal() {
    modals.open({
      name: ENROLLMENT_MODAL_KEYS.INITIAL,
      component: PaymentOnSellThroughEnrollmentModal,
      attributes: {
        onConfirm: async () => {
          await acknowledgeEnrollment();
          modals.close({ name: ENROLLMENT_MODAL_KEYS.INITIAL });
        },
        onReject: () => {
          modals.close({ name: ENROLLMENT_MODAL_KEYS.INITIAL });
          modals.open({
            name: ENROLLMENT_MODAL_KEYS.REJECT,
            component: PostEnrollmentNotInterestedModal,
            attributes: {
              onSubmit: async (text: string) => {
                await rejectEnrollment({ text });
                modals.close({ name: ENROLLMENT_MODAL_KEYS.REJECT });
              },
              onBack: () => {
                openEnrollmentModal();
                modals.close({ name: ENROLLMENT_MODAL_KEYS.REJECT });
              },
            },
          });
        },
      },
    });
  }

  function onClickTerms() {
    modals.open({
      name: ENROLLMENT_MODAL_KEYS.TERMS,
      component: PostEnrollmentTermsAndConditions,
      attributes: {
        onAccept: async () => {
          await acceptTerms();
          modals.close({ name: ENROLLMENT_MODAL_KEYS.TERMS });
          if (!dropdown.value?.isActive) {
            dropdown.value?.toggle();
          }
        },
        onCancel: () => modals.close({ name: ENROLLMENT_MODAL_KEYS.TERMS }),
      },
    });
  }

  watch(enrollmentQuery, (_, oldValue) => {
    if (
      // After acknowledging enrollment, present terms if they are not yet accepted
      (oldValue?.sellThroughStatus === 'initial' || oldValue?.sellThroughStatus === 'opt_out') &&
      enrollmentQuery.value?.sellThroughStatus === 'in_progress' &&
      enrollmentQuery.value?.terms?.accepted !== true
    ) {
      onClickTerms();
    } else if (
      // When the enrollment state changes to enabled, we want to open the dropdown and show the completion message
      enrollmentQuery.value?.sellThroughStatus === 'enabled' &&
      enrollmentQuery.value?.showCompletionMessage
    ) {
      showDropdown.value = true;
      if (dropdown.value?.isActive !== true) {
        dropdown.value?.toggle();
      }
      removeEnrollmentCompletionMessage();
    } else if (
      // handles opening up the dropdown after navigating back from bank linking or when the user clicks the PostEnrollmentModule CTA
      route.query.post_enrollment === 'true' ||
      (oldValue?.sellThroughStatus === 'opt_out' && enrollmentQuery.value?.sellThroughStatus === 'in_progress')
    ) {
      dropdown.value?.toggle();
      delete route.query.post_enrollment;
    }
  });

  watch(
    () => route.query,
    (newQuery) => {
      if (newQuery.open_post_enrollment_dropdown === 'true') {
        // without a timeout the dropdown will flash open briefly and then close
        setTimeout(() => dropdown.value?.toggle(), 10);
      }

      const updatedQuery = { ...route.query };
      delete updatedQuery.open_post_enrollment_dropdown;
      router.replace({ query: updatedQuery });
    },
  );

  const termsStatus = computed<PostEnrollmentStepActionStatus>(() =>
    enrollmentQuery.value?.terms.accepted ? 'complete' : 'incomplete',
  );

  const bankStatusDetails = computed<{
    status: PostEnrollmentStepActionStatus;
    title: string;
    subtitle: string;
  }>(() => {
    if (!enrollmentQuery.value?.terms.accepted) {
      return {
        status: 'pending',
        title: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.linkBankTitle'),
        subtitle: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.signTermsSubtitle'),
      };
    }

    switch (enrollmentQuery.value?.bankAccountLinked.status) {
      case 'unlinked':
        return {
          status: 'incomplete',
          title: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.linkBankTitle'),
          subtitle: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.linkBankSubtitle'),
        };
      case 'pending':
        return {
          status: 'pending',
          title: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.pendingBankTitle'),
          subtitle: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.pendingBankSubtitle'),
        };
      case 'pending_automatic_verification':
        return {
          status: 'pending',
          title: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.pendingBankTitle'),
          subtitle: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.pendingBankSubtitle'),
        };
      case 'pending_manual_verification':
        return {
          status: 'incomplete',
          title: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.verifyBankTitle'),
          subtitle: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.linkBankSubtitle'),
        };
      case 'linked':
        return {
          status: 'complete',
          title: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.linkedBankTitle'),
          subtitle: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.linkedBankSubtitle'),
        };
      default:
        return {
          status: 'incomplete',
          title: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.linkBankTitle'),
          subtitle: i18n.t('paymentOnSellThroughEnrollment.dropdown.bankLinkStep.linkBankSubtitle'),
        };
    }
  });

  const seedToSaleStatus = computed<PostEnrollmentStepActionStatus>(() =>
    enrollmentQuery.value?.seedToSaleLinked.status === 'linked' ? 'complete' : 'incomplete',
  );

  const stepsCount = computed(() => enrollmentQuery.value?.enrollmentStepCount);
  const learnMoreUrl = computed(() =>
    activeCompany.value?.seller
      ? i18n.t('paymentOnSellThroughEnrollment.sellerLearnMoreUrl')
      : i18n.t('paymentOnSellThroughEnrollment.buyerLearnMoreUrl'),
  );

  const onBankLinkRedirect = () => {
    // we want the link back to marketplace and the context switcher to
    // send the user back to their active company's default home page (dashboard)
    const currentCompanyBaseUrl = `${window.location.origin}/c/${activeCompany.value?.slug}?post_enrollment=true`;

    // sets the last session in the same way the ContextSwitcher does, this ensure that the context switcher
    // navigates the user back to the correct company when they return to the marketplace
    // reference: https://github.com/LeafLink/stash/blob/main/src/components/ContextSwitcher/ContextSwitcher.vue
    setPersistentItem(`${APPS.MARKETPLACE}-last-session`, currentCompanyBaseUrl, { global: true });
    isRedirecting.value = true;

    const query = {
      company_type: activeCompany.value?.seller ? 'seller' : 'buyer',
      company_id: activeCompany.value?.id,
      post_enrollment: 'true',
      open_bank_linking: 'false',
    };

    if (enrollmentQuery.value?.bankAccountLinked.status === 'unlinked') {
      query.open_bank_linking = 'true';
    }

    const route = router.resolve({ name: ROUTE_NAMES.PAYMENTS_ACCOUNT_SETTINGS, query });

    // redirect the user to payments via /accounts/login to ensure their MP session is re-created, ensuring their
    // user meta is up to date and portal access meta is up to date!
    const newWindow = window.open(`/accounts/login?next=${encodeURIComponent(route.href)}`, '_self');

    if (!newWindow) {
      isRedirecting.value = false;
      toasts.error(i18n.t('paymentOnSellThroughEnrollment.dropdown.redirectError'));
    }
  };

  const onConnectMetrc = (dismiss: () => void) => {
    dismiss();
    router.push({ name: ROUTE_NAMES.LICENSES });
  };

  const enrollmentCompleted = computed(() => enrollmentQuery.value?.sellThroughStatus === 'enabled');

  const dropdownTitle = computed(() =>
    enrollmentCompleted.value
      ? i18n.t('paymentOnSellThroughEnrollment.dropdown.headerEnrolled')
      : i18n.t('paymentOnSellThroughEnrollment.dropdown.header'),
  );

  const enrollmentCompletedDescription = computed(() =>
    activeCompany.value?.seller
      ? i18n.t('paymentOnSellThroughEnrollment.dropdown.descriptionEnrolled.seller')
      : i18n.t('paymentOnSellThroughEnrollment.dropdown.descriptionEnrolled.buyer'),
  );

  const enrollmentCompletedCTA = computed(() =>
    activeCompany.value?.seller
      ? i18n.t('paymentOnSellThroughEnrollment.dropdown.completionCTA.seller')
      : i18n.t('paymentOnSellThroughEnrollment.dropdown.completionCTA.buyer'),
  );

  const onClickEnrollmentCompletedCTA = function () {
    if (dropdown.value?.isActive) {
      dropdown.value?.toggle();
    }

    if (activeCompany.value?.seller) {
      router.push({
        name: ROUTE_NAMES.CUSTOMERS,
        params: { company: activeCompany.value?.slug },
        query: { post_enrollment: 'true' },
      });
    } else {
      router.push({
        name: ROUTE_NAMES.SHOP_BRANDS,
        params: { company: activeCompany.value?.slug },
        query: { post_enrollment: 'true' },
      });
    }
  };

  function onClickNotInterested() {
    modals.open({
      name: ENROLLMENT_MODAL_KEYS.REJECT,
      component: PostEnrollmentNotInterestedModal,
      attributes: {
        canClose: true,
        onSubmit: async (text: string) => {
          await rejectEnrollment({ text });
          modals.close({ name: ENROLLMENT_MODAL_KEYS.REJECT });
        },
        onBack: () => {
          modals.close({ name: ENROLLMENT_MODAL_KEYS.REJECT });
        },
      },
    });
  }

  const showEnrollmentDropdown = computed(() => {
    if (showDropdown.value === true) {
      return true;
    }

    if (
      enrollmentQuery.value?.sellThroughStatus === 'initial' ||
      enrollmentQuery.value?.sellThroughStatus === 'in_progress'
    ) {
      return true;
    }

    return false;
  });

  function onClickDropdown() {
    if (enrollmentQuery.value?.sellThroughStatus === 'initial') {
      openEnrollmentModal();
    } else {
      dropdown.value?.toggle();
    }
  }
</script>

<template>
  <Dropdown
    ref="dropdown"
    fluid-content
    :reattach="false"
    :content-class="['tw-border-none']"
    :class="{ 'tw-hidden': !showEnrollmentDropdown }"
    data-test="post-enrollment-dropdown"
  >
    <template #toggle>
      <Badge
        class="tw-cursor-pointer"
        :content="stepsCount"
        animate
        data-test="post-enrollment-dropdown-toggle"
        @click="onClickDropdown"
      >
        <Logo brand="payments" variant="brandmark" />
      </Badge>
    </template>
    <template #default="{ dismiss }">
      <Loading v-if="isFetching" class="tw-w-[340px]" />

      <div v-else class="tw-w-[340px]">
        <Button icon class="tw-p-0 tw-h-auto tw-absolute tw-right-1 tw-top-3 tw-m-0 tw-text-ice-900" @click="dismiss">
          <Icon name="close" />
        </Button>
        <div
          class="tw-bg-green-200 tw-rounded-t tw-bg-post-enrollment-dropdown-bg tw-px-6 tw-pt-4 tw-pb-1 tw-bg-[length:340px_auto]"
        >
          <Logo brand="post" variant="lockup" height="32" width="64" />
          <h2 class="tw-w-[240px] tw-relative tw-bottom-1">
            {{ dropdownTitle }}
          </h2>
        </div>
        <template v-if="!enrollmentCompleted">
          <div class="tw-p-6">
            <I18nT
              keypath="paymentOnSellThroughEnrollment.dropdown.description"
              scope="global"
              tag="p"
              data-test="post-enrollment-dropdown|description"
            >
              <template #link>
                <a :href="learnMoreUrl" target="_blank">
                  {{ i18n.t('paymentOnSellThroughEnrollment.descriptionLinkCopy') }}
                </a>
              </template>
            </I18nT>

            <Divider class="tw-my-6" />

            <p class="tw-mb-1">
              {{ i18n.t('paymentOnSellThroughEnrollment.dropdown.getStartedSteps') }}
            </p>
            <div class="tw-w-full tw-bg-ice-100 tw-rounded-full tw-h-2 tw-my-2">
              <div
                data-test="steps-progress-bar"
                class="tw-h-2 tw-rounded-full tw-bg-green-500"
                :class="[
                  'tw-rounded-l-full',
                  stepsCount ? 'tw-rounded-r-full' : 'tw-rounded-r-none', // if we have steps remaining we want to round the right side
                  {
                    'tw-w-0': stepsCount === 3 || stepsCount === undefined,
                    'tw-w-1/3': stepsCount === 2,
                    'tw-w-2/3': stepsCount === 1,
                    'tw-w-full': stepsCount === 0,
                  },
                ]"
              ></div>
            </div>
            <div>
              <PostEnrollmentStepAction
                data-test="step-action-terms"
                :status="termsStatus"
                :title="i18n.t('paymentOnSellThroughEnrollment.dropdown.acceptTermsTitle')"
                @click="onClickTerms"
              />
              <PostEnrollmentStepAction
                data-test="step-action-bank"
                :status="bankStatusDetails.status"
                class="tw-my-1"
                :title="bankStatusDetails.title"
                :subtitle="bankStatusDetails.subtitle"
                :is-loading="isRedirecting"
                @click="onBankLinkRedirect"
              />
              <PostEnrollmentStepAction
                data-test="step-action-seed-to-sale"
                :status="seedToSaleStatus"
                :title="i18n.t('paymentOnSellThroughEnrollment.dropdown.connectMetrcTitle')"
                :subtitle="i18n.t('paymentOnSellThroughEnrollment.dropdown.connectMetrcSubtitle')"
                @click="() => onConnectMetrc(dismiss)"
              />
              <Button inline class="tw-text-xs tw-text-ice-700 tw-mt-6" @click="onClickNotInterested">
                {{ i18n.t('paymentOnSellThroughEnrollment.dropdown.notInterested') }}
              </Button>
            </div>
          </div>
        </template>
        <div v-else class="tw-p-6">
          <p class="tw-mb-6 tw-text-ice-700 tw-text-base">{{ enrollmentCompletedDescription }}</p>
          <Button class="tw-w-full" @click="onClickEnrollmentCompletedCTA">{{ enrollmentCompletedCTA }}</Button>
        </div>
      </div>
    </template>
  </Dropdown>
</template>
